import useFormDetailsPage from '@/hooks/use-form-details-page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo, useState } from 'react';

import { useSilentRouter } from '@shared/hooks/use-router';
import { toast } from '@/shared/components/toast';
import { CUSTOM_STAGES } from '@/shared/constants/stage';

import {
	addFormStage,
	deleteFormStage,
	fetchFormCounts,
	renameFormStage,
	reorderFormStages,
} from '@/shared/services/forms/forms';
import { ISFetchFormCountsResponse, ISFormStage } from '@/shared/services/forms/forms.types';

import { GET_FORM_SUBMISSIONS_QUERY_KEY } from '@/utils/constants';

import ConfigureStages, { ConfigureStagesProps } from './ConfigureStages';

type FormConfigureStagesProps = Omit<
	ConfigureStagesProps,
	'stages' | 'onAdd' | 'onDelete' | 'onRename' | 'onReorder' | 'stagesCounts'
> & {
	fromMessagesPage?: boolean;
};

function FormConfigureStages({
	fromMessagesPage = false,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	...rest
}: FormConfigureStagesProps) {
	const router = useSilentRouter();
	const queryClient = useQueryClient();
	const [internalOpen, setInternalOpen] = useState(false);
	const open = controlledOpen ?? internalOpen;

	const { updatedFormDetails: formDetails, updateFormStages } = useFormDetailsPage();

	const formId = formDetails?.identifier ?? '';
	const formToken = formDetails?.formToken ?? '';
	const stages = useMemo(() => formDetails?.stages ?? [], [formDetails?.stages]);
	const fetchCountQueryKey = useMemo(() => ['form-counts', formId, formToken], [formId, formToken]);

	const { data: formCounts } = useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: fetchCountQueryKey,
		queryFn: () =>
			fetchFormCounts({
				form_id: formId,
				form_token: formToken,
				fetch_status_counts: true,
			}),
		enabled: !!formId,
	});


	const { mutate: addStageMutation } = useMutation({
		mutationFn: addFormStage,
		onSuccess: () => {
			toast({ title: 'Stage added successfully', variant: 'success' });
		},
	});

	const { mutate: reorderStagesMutation } = useMutation({
		mutationFn: reorderFormStages,
		onSuccess: () => {
			toast({ title: 'Stage reordered successfully', variant: 'success' });
		},
	});

	const { mutate: deleteStageMutation } = useMutation({
		mutationFn: deleteFormStage,
		onSuccess: () => {
			toast({ title: 'Stage deleted successfully', variant: 'success' });
		},
	});

	const { mutateAsync: renameStageMutation } = useMutation({
		mutationFn: renameFormStage,
		onSuccess: () => {
			toast({ title: 'Stage renamed successfully', variant: 'success' });
		},
	});

	const handleAdd = useCallback(
		(label: string) => {
			const freshStage: ISFormStage = {
				label,
				notificationsEnabled: false,
			};

			const newStage = stages.find((e) => e.label === CUSTOM_STAGES.NEW);
			const archivedStage = stages.find((e) => e.label === CUSTOM_STAGES.ARCHIVED);

			const updatedStages = [
				newStage,
				...stages.filter(
					(e) => e.label !== CUSTOM_STAGES.NEW && e.label !== CUSTOM_STAGES.ARCHIVED
				),
				freshStage,
				archivedStage,
			] as ISFormStage[];

			addStageMutation({ form_id: formId, label });
			updateFormStages(updatedStages, fromMessagesPage);
		},
		[stages, updateFormStages, formId, addStageMutation, fromMessagesPage]
	);

	const handleDelete = useCallback(
		(label: string) => {
			deleteStageMutation({ form_id: formId, label });
			const updatedStages = stages.filter((stage) => stage.label !== label);
			updateFormStages(updatedStages, fromMessagesPage);
		},
		[stages, updateFormStages, formId, deleteStageMutation, fromMessagesPage]
	);

	const handleRename = useCallback(
		async (oldLabel: string, newLabel: string) => {
			// Optimistic UI update first
			const updatedStages = stages.map((stage) =>
				stage.label === oldLabel ? { ...stage, label: newLabel } : stage
			);
			updateFormStages(updatedStages, fromMessagesPage);

			// Then API call
			await renameStageMutation({
				form_id: formId,
				old_label: oldLabel,
				new_label: newLabel,
			});

			// Update counts cache
			queryClient.setQueryData(fetchCountQueryKey, (oldData: ISFetchFormCountsResponse) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					statusCounts: oldData.statusCounts.map(
						(item: ISFetchFormCountsResponse['statusCounts'][number]) =>
							item.stage === oldLabel ? { ...item, stage: newLabel } : item
					),
				};
			});

			const stageHadApplicants = !!formCounts?.statusCounts?.find(
				(item) => item.stage === oldLabel
			)?.count;
			if (stageHadApplicants)
				queryClient.invalidateQueries({
					queryKey: [GET_FORM_SUBMISSIONS_QUERY_KEY, formId, formToken],
				});

			// Update URL tab param
			const params = new URLSearchParams(window.location?.href?.split('?')[1]);
			params.delete('tab');
			params.append('tab', newLabel);
			router.replace(`${window.location.pathname}?${params.toString()}`);
		},
		[
			formId,
			stages,
			updateFormStages,
			fromMessagesPage,
			renameStageMutation,
			queryClient,
			fetchCountQueryKey,
			formCounts,
			formToken,
			router,
		]
	);

	const handleReorder = useCallback(
		(reorderedStages: { label: string }[]) => {
			const newStage = stages.find((e) => e.label === CUSTOM_STAGES.NEW);
			const archivedStage = stages.find((e) => e.label === CUSTOM_STAGES.ARCHIVED);

			const netStages = [newStage, ...reorderedStages, archivedStage] as ISFormStage[];

			reorderStagesMutation({
				form_id: formId,
				stages: netStages.map((e) => e?.label ?? ''),
			});
			updateFormStages(netStages, fromMessagesPage);
		},
		[stages, reorderStagesMutation, formId, updateFormStages, fromMessagesPage]
	);

	const isControlled = controlledOpen !== undefined;

	const handleOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) queryClient.invalidateQueries({ queryKey: fetchCountQueryKey });
			if (!isControlled) setInternalOpen(isOpen);
			controlledOnOpenChange?.(isOpen);
		},
		[queryClient, fetchCountQueryKey, controlledOnOpenChange, isControlled]
	);

	return (
		<ConfigureStages
			stages={stages}
			stagesCounts={formCounts?.statusCounts || []}
			onAdd={handleAdd}
			onDelete={handleDelete}
			onRename={handleRename}
			onReorder={handleReorder}
			open={open}
			onOpenChange={handleOpenChange}
			{...rest}
		/>
	);
}

export default FormConfigureStages;
